
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const usePasswordReset = (setIsResettingPassword: (value: boolean) => void) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePasswordRecovery = async () => {
    // Check for password recovery flow
    const hash = window.location.hash;
    
    // Detect recovery flow from URL parameters
    if (hash && hash.includes('type=recovery')) {
      console.log('Password recovery flow detected');
      
      try {
        setIsResettingPassword(true);
        setIsRecoveryFlow(true);
        setShowResetDialog(true);
        // Prevent auto redirection by returning true
        return true;
      } catch (err) {
        console.error('Error initializing password reset:', err);
        toast({
          title: "Error",
          description: "Failed to initialize password reset. Please try again.",
          variant: "destructive",
        });
      }
    }
    return false;
  };

  const handleSendPasswordResetEmail = async () => {
    if (!resetPasswordEmail) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('Sending password reset email to:', resetPasswordEmail);
      
      // Use the edge function to send a reset email
      const resetLink = `${window.location.origin}/login#type=recovery`;
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { 
          email: resetPasswordEmail,
          resetLink: resetLink
        }
      });

      if (error) {
        console.error('Error invoking send-password-reset function:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to send password reset email",
          variant: "destructive",
        });
        return;
      }
      
      // Also trigger the built-in Supabase reset
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetPasswordEmail, {
        redirectTo: `${window.location.origin}/login`,
      });

      if (resetError) {
        console.error('Error sending password reset email:', resetError);
        toast({
          title: "Error",
          description: resetError.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Password Reset Email Sent",
          description: "Check your email for a link to reset your password.",
        });
      }
    } catch (err) {
      console.error('Error sending password reset email:', err);
      toast({
        title: "Error",
        description: "Failed to send password reset email. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePasswordReset = async () => {
    if (isSubmitting) return;
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('Updating password...');
      // Update user password directly
      const { data, error } = await supabase.auth.updateUser({ 
        password: newPassword 
      });

      if (error) {
        console.error('Error updating password:', error);
        toast({
          title: "Error resetting password",
          description: error.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
      } else {
        console.log('Password updated successfully');
        toast({
          title: "Password updated successfully",
          description: "You can now sign in with your new password.",
        });
        
        // Clear the hash fragment
        window.location.hash = '';
        
        // Reset state
        setShowResetDialog(false);
        setNewPassword("");
        setConfirmPassword("");
        setIsRecoveryFlow(false);
        
        // Sign out to ensure the user logs in with the new password
        await supabase.auth.signOut();
        setIsResettingPassword(false);
        setIsSubmitting(false);
        
        // Navigate without page reload to avoid resetting state
        navigate('/login', { replace: true });
      }
    } catch (err) {
      console.error('Error updating password:', err);
      toast({
        title: "Error",
        description: "Failed to update password. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  return {
    showResetDialog,
    setShowResetDialog,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    resetPasswordEmail,
    setResetPasswordEmail,
    handlePasswordRecovery,
    handlePasswordReset,
    handleSendPasswordResetEmail,
    isRecoveryFlow,
    isSubmitting
  };
};
